import { ArrowRightIcon, CheckIcon, MonitorIcon } from "lucide-react";
import { scopedProjectKey, scopeProjectRef } from "@cmmd-forge/client-runtime";

import type { CmmdForgeRuntimeListSnapshot } from "../../cmmd/cmmdForgeRuntimeListTypes";
import type { Project } from "../../types";
import { Button } from "../ui/button";
import type { ProviderSetupRow } from "./firstAccountSetupData";

/**
 * The welcome wizard's step panels, split from the wizard shell
 * (`WelcomeWizard.tsx`). Each panel is presentational: it renders the state
 * the shell hands it and reports intent through its callbacks. Ported from
 * upstream's `WelcomeWizard.tsx` steps onto forge's data (forge environments,
 * provider rows, project rows).
 */

type ConnectEnvironmentRow = CmmdForgeRuntimeListSnapshot["environments"][number];

export function projectKey(project: Project): string {
  return scopedProjectKey(scopeProjectRef(project.environmentId, project.id));
}

function environmentLabel(environment: {
  readonly displayName: string | null;
  readonly userFirstName: string | null;
}): string {
  if (environment.displayName !== null) {
    return environment.displayName;
  }
  if (environment.userFirstName !== null) {
    return `${environment.userFirstName}'s Environment`;
  }
  return "Environment";
}

export function ConnectStep({
  environments,
  onContinue,
}: {
  readonly environments: readonly ConnectEnvironmentRow[];
  readonly onContinue: () => void;
}) {
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Welcome to Forge</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
        Your Forge Environments are where your work runs. You can add more at any time.
      </p>
      {environments.length > 0 ? (
        <fieldset className="mt-5 space-y-2">
          <legend className="sr-only">Forge Environments</legend>
          {environments.map((environment) => (
            <div
              key={environment.environmentId}
              className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-3"
            >
              <MonitorIcon className="size-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">
                {environmentLabel(environment)}
              </span>
              <span className="shrink-0 text-xs text-muted-foreground">{environment.status}</span>
            </div>
          ))}
        </fieldset>
      ) : (
        <p className="mt-5 rounded-lg border border-border bg-background px-3 py-3 text-sm text-muted-foreground">
          No Environments yet. You can add one after setup from the composer.
        </p>
      )}
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button autoFocus onClick={onContinue}>
          Continue
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </div>
    </>
  );
}

export function AgentsStep({
  providerRows,
  onContinue,
}: {
  readonly providerRows: readonly ProviderSetupRow[];
  readonly onContinue: () => void;
}) {
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Your agents</h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
        Codex and Claude availability on your Environment. You can connect accounts after setup.
      </p>
      {providerRows.length > 0 ? (
        <div className="mt-5 space-y-1.5">
          {providerRows.map((row) => (
            <AgentStatusRow key={row.id} row={row} />
          ))}
        </div>
      ) : (
        <p className="mt-5 text-sm text-muted-foreground">No providers configured yet.</p>
      )}
      <div className="mt-6 flex items-center justify-end gap-3">
        <Button autoFocus onClick={onContinue}>
          Continue
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </div>
    </>
  );
}

function AgentStatusRow({ row }: { readonly row: ProviderSetupRow }) {
  return (
    <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5">
      <span className={statusDotClass(row.statusTone)} aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-foreground">{row.name}</span>
        <p className="mt-0.5 text-xs text-muted-foreground">{row.statusLabel}</p>
      </div>
    </div>
  );
}

function statusDotClass(tone: ProviderSetupRow["statusTone"]): string {
  if (tone === "ready") return "size-2 shrink-0 rounded-full bg-success";
  if (tone === "attention") return "size-2 shrink-0 rounded-full bg-warning";
  return "size-2 shrink-0 rounded-full bg-border";
}

export function ProjectsStep({
  projects,
  selectedProjectKey,
  isFinishing,
  onSelectionChange,
  onSkip,
  onStart,
}: {
  readonly projects: readonly Project[];
  readonly selectedProjectKey: string | null;
  readonly isFinishing: boolean;
  readonly onSelectionChange: (key: string) => void;
  readonly onSkip: () => void;
  readonly onStart: () => void;
}) {
  return (
    <>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">
        Choose your first project
      </h1>
      <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
        Pick where your first thread starts, or add projects later.
      </p>
      {projects.length > 0 ? (
        <fieldset className="mt-5 space-y-2" disabled={isFinishing}>
          <legend className="sr-only">Projects</legend>
          {projects.map((project) => {
            const key = projectKey(project);
            const selected = key === selectedProjectKey;
            return (
              <button
                key={key}
                type="button"
                disabled={isFinishing}
                aria-pressed={selected}
                onClick={() => onSelectionChange(key)}
                className={
                  selected
                    ? "flex w-full items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-3 py-3 text-left"
                    : "flex w-full items-center gap-3 rounded-lg border border-border bg-background px-3 py-3 text-left"
                }
              >
                {selected ? (
                  <CheckIcon className="size-4 shrink-0 text-primary" />
                ) : (
                  <span className="size-4 shrink-0" aria-hidden />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{project.name}</span>
                  <span className="mt-0.5 block truncate font-mono text-xs text-muted-foreground">
                    {project.cwd}
                  </span>
                </span>
              </button>
            );
          })}
        </fieldset>
      ) : (
        <p className="mt-5 rounded-lg border border-border bg-background px-3 py-3 text-sm text-muted-foreground">
          No projects yet. The composer can add your first one.
        </p>
      )}
      <div className="mt-6 flex flex-wrap items-center justify-end gap-3">
        <Button variant="ghost-muted" disabled={isFinishing} onClick={onSkip}>
          Do this later
        </Button>
        <Button
          autoFocus
          disabled={isFinishing || (projects.length > 0 && selectedProjectKey === null)}
          onClick={onStart}
        >
          {isFinishing ? "Setting up..." : "Start first thread"}
          <ArrowRightIcon className="size-3.5" />
        </Button>
      </div>
    </>
  );
}
